import { createFileRoute } from '@tanstack/react-router';
import { formatDistanceToNowStrict } from 'date-fns';
import {
    ArrowUpDown,
    CircleAlert,
    Clock4,
    Factory,
    RefreshCw,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { DataTableToolbar } from '@apps/web/components/ui/data_table_toolbar';
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
} from '@apps/web/components/ui/pagination';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    type AppTableFeatures,
    appTableFeatures,
    type ColumnFiltersState,
    createColumnHelper,
    flexRender,
    type SortingState,
    useTable,
} from '@apps/web/lib/table';
import { cn } from '@apps/web/lib/utils';
import { useAdminOutboxEntries } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AdminOutboxEntryListResponse,
    type AdminOutboxEntryRead,
} from '@libraries/typescript/api_client';

const PER_PAGE = 50;

export const Route = createFileRoute('/_app/admin/outbox/')({
    component: OutboxPage,
    head: () => ({
        meta: [
            {
                title: 'Outbox | Phoebe Admin',
            },
        ],
    }),
});

const columnHelper = createColumnHelper<
    AppTableFeatures,
    AdminOutboxEntryRead
>();

function OutboxPage() {
    const [page, setPage] = useState(1);
    const [sorting, setSorting] = useState<SortingState>([
        { id: 'created_at', desc: true },
    ]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState('');

    const outboxQuery = useAdminOutboxEntries(
        { page, perPage: PER_PAGE },
        { keepPreviousData: true },
    );
    const { isLoading, isValidating, mutate } = outboxQuery;
    const data = outboxQuery.data as AdminOutboxEntryListResponse | undefined;

    const handleRefresh = useCallback(() => {
        void mutate();
    }, [mutate]);

    const totalEntries = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalEntries / PER_PAGE));

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const columns = useMemo(
        () =>
            columnHelper.columns([
                columnHelper.accessor('organization_name', {
                    header: 'Organization',
                    cell: (info) => info.getValue(),
                }),
                columnHelper.accessor('channel', {
                    header: 'Channel',
                    cell: (info) => (
                        <Badge variant="outline" className="capitalize">
                            {info.getValue().toLowerCase()}
                        </Badge>
                    ),
                }),
                columnHelper.accessor('status', {
                    header: 'Status',
                    cell: (info) => {
                        const status = info.getValue();
                        const variant =
                            status === 'dead_letter'
                                ? 'destructive'
                                : status === 'failed'
                                  ? 'secondary'
                                  : 'outline';
                        return (
                            <Badge variant={variant} className="capitalize">
                                {status.replace('_', ' ').toLowerCase()}
                            </Badge>
                        );
                    },
                }),
                columnHelper.accessor('attempts', {
                    header: () => (
                        <div className="text-muted-foreground flex items-center gap-1 text-xs font-medium">
                            <Factory className="h-3 w-3" />
                            Attempts
                        </div>
                    ),
                    cell: ({ row }) => (
                        <span className="text-sm">
                            {row.original.attempts} /{' '}
                            {row.original.max_attempts}
                        </span>
                    ),
                }),
                columnHelper.accessor('payload_summary', {
                    header: 'Provider / Dest',
                    cell: ({ row }) => {
                        const summary = row.original.payload_summary;
                        if (!summary) {
                            return (
                                <span className="text-muted-foreground text-xs">
                                    —
                                </span>
                            );
                        }

                        const provider =
                            typeof summary['provider'] === 'string'
                                ? summary['provider']
                                : null;
                        const toNumber =
                            typeof summary['to_number'] === 'string'
                                ? summary['to_number']
                                : null;
                        const fromNumber =
                            typeof summary['from_number'] === 'string'
                                ? summary['from_number']
                                : null;
                        return (
                            <div className="text-muted-foreground flex flex-col text-xs">
                                <span className="text-foreground font-medium">
                                    {provider ?? 'unknown'}
                                </span>
                                {toNumber && <span>to: {toNumber}</span>}
                                {fromNumber && <span>from: {fromNumber}</span>}
                            </div>
                        );
                    },
                }),
                columnHelper.accessor('next_attempt_at', {
                    header: () => (
                        <div className="text-muted-foreground flex items-center gap-1 text-xs font-medium">
                            <Clock4 className="h-3 w-3" />
                            Next Attempt
                        </div>
                    ),
                    cell: ({ row }) => {
                        const next = row.original.next_attempt_at;
                        if (!next) {
                            return (
                                <span className="text-muted-foreground text-xs">
                                    —
                                </span>
                            );
                        }
                        const nextDate = new Date(next);
                        return (
                            <Tooltip>
                                <TooltipTrigger className="text-muted-foreground text-xs">
                                    {formatDistanceToNowStrict(nextDate, {
                                        addSuffix: true,
                                    })}
                                </TooltipTrigger>
                                <TooltipContent>
                                    {nextDate.toLocaleString()}
                                </TooltipContent>
                            </Tooltip>
                        );
                    },
                }),
                columnHelper.accessor('created_at', {
                    header: ({ column }) => (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="-ml-3 h-6 text-xs"
                            onClick={column.getToggleSortingHandler()}
                        >
                            <ArrowUpDown className="mr-1 h-3 w-3" />
                            Created
                        </Button>
                    ),
                    cell: ({ row }) => {
                        const created = new Date(row.original.created_at);
                        return (
                            <Tooltip>
                                <TooltipTrigger className="text-muted-foreground text-xs">
                                    {formatDistanceToNowStrict(created, {
                                        addSuffix: true,
                                    })}
                                </TooltipTrigger>
                                <TooltipContent>
                                    {created.toLocaleString()}
                                </TooltipContent>
                            </Tooltip>
                        );
                    },
                }),
            ]),
        [],
    );

    const tableData = useMemo(
        () => Array.from(data?.outbox_entries ?? []),
        [data],
    );

    const table = useTable({
        features: appTableFeatures,
        data: tableData,
        columns,
        state: {
            sorting,
            columnFilters,
            globalFilter,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        manualPagination: true,
    });

    const isFetching = isValidating && !isLoading;

    return (
        <div className="flex flex-1 flex-col gap-4 p-6">
            <Card className="shadow-sm">
                <CardHeader className="border-b">
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-lg">Outbox</CardTitle>
                            <CardDescription>
                                Track queued voice and SMS jobs, retry cadence,
                                and downstream errors.
                            </CardDescription>
                        </div>
                        <div className="text-muted-foreground flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="gap-1 text-xs"
                                onClick={handleRefresh}
                                disabled={isValidating}
                            >
                                <RefreshCw
                                    className={cn(
                                        'h-3 w-3',
                                        isValidating ? 'animate-spin' : '',
                                    )}
                                />
                                Refresh
                            </Button>
                            {isFetching && <Skeleton className="h-4 w-8" />}
                            <Badge variant="outline" className="gap-1 text-xs">
                                <CircleAlert className="h-3 w-3" />
                                {totalEntries} total
                            </Badge>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="flex flex-col gap-3 p-0">
                    <div className="border-b px-6 py-3">
                        <DataTableToolbar
                            table={table}
                            globalFilterPlaceholder="Search outbox entries…"
                        />
                    </div>
                    <div className="flex-1 px-6 pb-6">
                        <div className="overflow-hidden rounded-md border">
                            <table className="divide-border min-w-full divide-y text-sm">
                                <thead className="bg-muted/40">
                                    {table
                                        .getHeaderGroups()
                                        .map((headerGroup) => (
                                            <tr key={headerGroup.id}>
                                                {headerGroup.headers.map(
                                                    (header) => (
                                                        <th
                                                            key={header.id}
                                                            className="text-muted-foreground px-3 py-2 text-left text-[10px] font-semibold tracking-wide uppercase"
                                                        >
                                                            {header.isPlaceholder
                                                                ? null
                                                                : flexRender(
                                                                      header
                                                                          .column
                                                                          .columnDef
                                                                          .header,
                                                                      header.getContext(),
                                                                  )}
                                                        </th>
                                                    ),
                                                )}
                                            </tr>
                                        ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map((row) => (
                                        <tr
                                            key={row.id}
                                            className="even:bg-muted/10 odd:bg-background"
                                        >
                                            {row
                                                .getVisibleCells()
                                                .map((cell) => (
                                                    <td
                                                        key={cell.id}
                                                        className="px-3 py-2 align-top"
                                                    >
                                                        {flexRender(
                                                            cell.column
                                                                .columnDef.cell,
                                                            cell.getContext(),
                                                        )}
                                                    </td>
                                                ))}
                                        </tr>
                                    ))}
                                    {table.getRowModel().rows.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={columns.length}
                                                className="text-muted-foreground px-3 py-12 text-center text-xs"
                                            >
                                                {isLoading
                                                    ? 'Loading outbox entries…'
                                                    : 'No outbox entries found for the selected filters.'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="text-muted-foreground flex items-center justify-between border-t px-6 py-4 text-xs">
                        <span>
                            Showing {table.getRowModel().rows.length} of{' '}
                            {totalEntries} items
                        </span>
                        <Pagination>
                            <PaginationContent>
                                <PaginationItem>
                                    <PaginationPrevious
                                        href="#"
                                        aria-disabled={page === 1}
                                        className={
                                            page === 1
                                                ? 'pointer-events-none opacity-50'
                                                : ''
                                        }
                                        onClick={(event) => {
                                            event.preventDefault();
                                            setPage((prev) =>
                                                Math.max(1, prev - 1),
                                            );
                                        }}
                                    />
                                </PaginationItem>
                                <PaginationItem>
                                    <span className="px-2 py-1 text-sm font-medium">
                                        Page {page}
                                    </span>
                                </PaginationItem>
                                <PaginationItem>
                                    <PaginationNext
                                        href="#"
                                        aria-disabled={page >= totalPages}
                                        className={
                                            page >= totalPages
                                                ? 'pointer-events-none opacity-50'
                                                : ''
                                        }
                                        onClick={(event) => {
                                            event.preventDefault();
                                            setPage((prev) =>
                                                Math.min(totalPages, prev + 1),
                                            );
                                        }}
                                    />
                                </PaginationItem>
                            </PaginationContent>
                        </Pagination>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default OutboxPage;
